/**
 * PresenceContext
 *
 * Tracks which users in the caller's company are currently online.
 * Subscribes to the singleton WebSocket via useWebSocket() and listens for:
 *   - `connected` (initial snapshot, contains `online_user_ids`)
 *   - `presence_update` ({user_id, online})
 *
 * Falls back to GET /api/presence/online once on mount in case the WS
 * `connected` payload was missed (e.g. component mounts after handshake).
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import { useWebSocketSubscribe } from './WebSocketContext';

const PresenceContext = createContext(null);

export const PresenceProvider = ({ children }) => {
  const { user, api } = useAuth();
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());

  // Bootstrap from REST once the user is logged in
  useEffect(() => {
    if (!user?.user_id) {
      setOnlineUserIds(new Set());
      return;
    }
    let cancelled = false;
    api.get('/presence/online')
      .then((res) => {
        if (cancelled) return;
        const ids = res.data?.online_user_ids || [];
        setOnlineUserIds(new Set(ids));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.user_id, api]);

  const handleWsMessage = useCallback((data) => {
    if (!data) return;
    if (data.type === 'connected' && Array.isArray(data.online_user_ids)) {
      // Replace only if actually different — prevents needless re-render when
      // an existing client reconnects with the same online roster.
      setOnlineUserIds((prev) => {
        const incoming = new Set(data.online_user_ids);
        if (prev.size === incoming.size) {
          let same = true;
          for (const id of incoming) {
            if (!prev.has(id)) { same = false; break; }
          }
          if (same) return prev;
        }
        return incoming;
      });
      return;
    }
    if (data.type === 'presence_update' && data.user_id) {
      setOnlineUserIds((prev) => {
        const wantsOnline = !!data.online;
        const has = prev.has(data.user_id);
        // No-op when state already matches — avoids re-render storms
        if (wantsOnline === has) return prev;
        const next = new Set(prev);
        if (wantsOnline) {
          next.add(data.user_id);
        } else {
          next.delete(data.user_id);
        }
        return next;
      });
    }
  }, []);

  useWebSocketSubscribe(handleWsMessage);

  const value = useMemo(
    () => ({
      onlineUserIds,
      isOnline: (uid) => onlineUserIds.has(uid),
      onlineCount: onlineUserIds.size,
    }),
    [onlineUserIds]
  );

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

export const usePresence = () => {
  const ctx = useContext(PresenceContext);
  if (!ctx) {
    // Safe fallback when used outside provider — never blow up
    return { onlineUserIds: new Set(), isOnline: () => false, onlineCount: 0 };
  }
  return ctx;
};

export default PresenceContext;
